import { createActionHelpers } from 'vuex-loading';

const { startLoading, endLoading } = createActionHelpers({
    moduleName: 'loading',
});

/*
 * Format the active reconciliation date for API Request to wetstock report
 */
const formatReconciliationDate = date => {
    const reconciliationDate = new Date(
        state.activeReconciliation.reconciliation_date,
    );

    reconciliationDate.setHours(0, 0, 0);
    const since = reconciliationDate.toISOString().replace('.000Z', '');

    reconciliationDate.setHours(23, 59, 59);
    const until = reconciliationDate.toISOString().replace('.000Z', '');

    return { since, until };
};

const formatDate = date => {
    const reconciliationDate = new Date(date);
    const since = reconciliationDate.toISOString().replace(/\.\d{3}/, '');
    const until = new Date(
        Date.UTC(
            reconciliationDate.getUTCFullYear(),
            reconciliationDate.getUTCMonth(),
            reconciliationDate.getUTCDate(),
            23,
            59,
            59,
            999,
        ),
    )
        .toISOString()
        .replace(/\.\d{3}/, '');

    return { since, until };
};

const findTankIndex = (reconciliationDetails, tankId) =>
    reconciliationDetails.findIndex(tank => tank.tank_id == tankId.toString());

const state = {
    filters: [],
    searchMeta: null,
    reconciliationStatus: [],
    activeReconciliation: null,
    reconciliationDetails: [],
    pumpTestData: null,
};

const ACTION_TYPES = {
    UPDATE_DAILY_RECONCILIATION_DETAIL: 'UPDATE_DAILY_RECONCILIATION_DETAIL',
};

const getters = {
    filters: state => state.filters,
    reconciliationStatus: state => state.reconciliationStatus,
    activeReconciliation: state => state.activeReconciliation,
    reconciliationDetails: state => state.reconciliationDetails,
    pumpTestData: state => state.pumpTestData,
};

const mutations = {
    setReconciliationStatus: (state, data) =>
        (state.reconciliationStatus = data),
    setFilters: (state, filters) => (state.filters = filters),
    setSearchMeta: (state, meta) => (state.searchMeta = meta),
    setActiveReconciliation: (state, reconciliation) =>
        (state.activeReconciliation = reconciliation),
    setReconciliationDetails: (state, details) =>
        (state.reconciliationDetails = details),
    [ACTION_TYPES.UPDATE_DAILY_RECONCILIATION_DETAIL]: (
        state,
        { tankId, attr, value },
    ) => {
        if (state.reconciliationDetails.length) {
            const tankIndex = findTankIndex(
                state.reconciliationDetails,
                tankId,
            );
            state.reconciliationDetails[tankIndex][attr] = value;
        }
    },
    setPumpTestData: (state, data) => (state.pumpTestData = data),
};

const actions = {
    getDailyReconciliationMeta({ commit, dispatch }) {
        startLoading(dispatch, 'get-daily-reconciliation-meta');
        const meta = {
            config: [],
            filters: [
                {
                    name: 'outlet_id',
                    display_name: 'Station',
                    group_display_name: 'Station',
                    operators: [
                        {
                            name: 'is',
                            display_name: 'is',
                            type: 'list',
                        },
                        {
                            name: 'is_not',
                            display_name: 'is not',
                            type: 'list',
                        },
                        {
                            name: 'includes',
                            display_name: 'includes',
                            type: 'multiselect',
                        },
                        {
                            name: 'does_not_include',
                            display_name: 'does not include',
                            type: 'multiselect',
                        },
                    ],
                    remote: {
                        external: true,
                        name: 'outlet',
                    },
                },
                {
                    name: 'flag',
                    display_name: 'Daily Reconciliation Status',
                    group_display_name: 'Status',
                    operators: [
                        {
                            name: 'is',
                            display_name: 'is',
                            type: 'list',
                        },
                        {
                            name: 'includes',
                            display_name: 'includes',
                            type: 'multiselect',
                        },
                    ],
                    remote: {
                        external: true,
                        name: 'state',
                    },
                },
            ],
            sort_fields: [],
        };

        commit('setSearchMeta', meta);
        endLoading(dispatch, 'get-daily-reconciliation-meta');
        return meta;
    },

    getSearchMetaIfNecessary: ({ dispatch, state }) =>
        state.searchMeta
            ? Promise.resolve(state.searchMeta)
            : dispatch('getDailyReconciliationMeta'),

    getDailyReconciliationStatusList({ commit, dispatch }) {
        startLoading(dispatch, 'fetch-daily-reconciliation-status');

        const status = [
            {
                flag: 'success',
                name: 'Success',
            },
            {
                flag: 'failed',
                name: 'Failed',
            },
            {
                flag: 'new',
                name: 'New',
            },
        ];

        commit('setReconciliationStatus', status);
        endLoading(dispatch, 'fetch-daily-reconciliation-status');

        return status;
    },

    getDailyReconciliationStatus({ dispatch }) {
        if (!state.reconciliationStatus.length) {
            return dispatch('getDailyReconciliationStatusList');
        }

        return Promise.resolve(state.reconciliationStatus);
    },

    fetchActiveReconciliation({ commit }, id) {
        return axios
            .get(
                Laroute.route(
                    'franchise.inventory.fuel.daily-reconciliation.report',
                    { id },
                ),
            )
            .then(response => {
                commit('setActiveReconciliation', response.data);
                return response.data;
            });
    },

    setActionReconciliationDetails({ commit, state }, details) {
        if (state.reconciliationDetails && details.length > 0) {
            const pumpTestTotals = state.pumpTestData.reduce((acc, data) => {
                const tankId = data.return_tank_id;
                if (!acc[tankId]) {
                    acc[tankId] = 0;
                }
                acc[tankId] += parseFloat(data.final_volume) || 0;
                return acc;
            }, {});

            let receivingDetails = details.map((detail, index) => {
                const prevDetail = index > 0 ? details[index - 1] : null;
                const isMatchingGroup =
                    prevDetail &&
                    prevDetail.tank_group_id === detail.tank_group_id;

                const tankPumpTestVolume = pumpTestTotals[detail.code];

                return {
                    group_id: isMatchingGroup ? '' : detail.tank_group_id,
                    tank_id: detail.id,
                    tank_code: detail.code,
                    fuel_name: detail.fuel_name,
                    opening_stock: 0,
                    receiving: 0,
                    other: tankPumpTestVolume ? tankPumpTestVolume : 0,
                    sales: isMatchingGroup ? '' : 0,
                    closing_stock: 0,
                    actual_closing_stock: isMatchingGroup ? '' : [],
                    variance: isMatchingGroup ? '' : 0,
                };
            });
            commit('setReconciliationDetails', receivingDetails);
        } else {
            commit('setReconciliationDetails', []);
        }
    },

    fetchPumpTestResult({ commit, dispatch, state }) {
        const { since, until } = formatDate(
            state.activeReconciliation.reconciliation_date,
        );

        return axios
            .get(Laroute.route('franchise.report.fuel.pump-test.getPumpTest'), {
                params: {
                    outlet_id: state.activeReconciliation.outlet.id,
                    since: since,
                    until: until,
                    limit: 1000,
                },
            })
            .then(response => {
                commit('setPumpTestData', response.data.data);
            });
    },

    fetchTankTurnoverRates({ commit, state }, tankIds) {
        const { since, until } = formatReconciliationDate();

        const api = Laroute.route(
            'franchise.inventory.fuel.daily-reconciliation.report.turnover',
        );

        const options = {
            franchise_id: state.activeReconciliation.outlet.franchise_id,
            tank_ids: tankIds,
            since,
            until,
        };

        return axios
            .post(api, options)
            .then(response => {
                const turnoverDatas = response.data.data;

                if (turnoverDatas) {
                    const processedGroups = new Set();

                    turnoverDatas.forEach(turnoverData => {
                        const tankDetail = state.reconciliationDetails.find(
                            detail =>
                                detail.tank_id.toString() ===
                                turnoverData.tank_id.toString(),
                        );

                        /* 
                     * Manifold Tank: Sales column commit implementation
                     * Only commit once per tank group_id by checking if the group_id has been processed
                     */
                        if (tankDetail) {
                            const groupId = tankDetail.group_id;

                            if (groupId && !processedGroups.has(groupId)) {
                                processedGroups.add(groupId);

                                commit(
                                    ACTION_TYPES.UPDATE_DAILY_RECONCILIATION_DETAIL,
                                    {
                                        tankId: turnoverData.tank_id,
                                        attr: 'sales',
                                        value: parseFloat(turnoverData.volume),
                                    },
                                );
                            }
                        }
                    });
                }

                return response.data;
            })
            .catch(error => {
                console.error(
                    'Error in fetchTankTurnoverRates:',
                    error.response.data,
                );
            });
    },

    fetchTankReceiving({ commit, state }) {
        const { since, until } = formatDate(
            state.activeReconciliation.reconciliation_date,
        );

        return axios
            .get(
                Laroute.route('franchise.inventory.fuel.receiving.list', {
                    outlet_id: state.activeReconciliation.outlet.id,
                    limit: 1,
                    page: 1,
                    since,
                    until,
                }),
            )
            .then(response => {
                const receivingResult = response.data.data;
                if (receivingResult) {
                    receivingResult.forEach(receivings => {
                        // only not deleted and completed receivings
                        if (
                            !receivings.deleted_at &&
                            receivings.status === 'COMPLETED' &&
                            receivings.receivings
                        ) {
                            receivings.receivings.forEach(receiving => {
                                commit(
                                    ACTION_TYPES.UPDATE_DAILY_RECONCILIATION_DETAIL,
                                    {
                                        tankId: receiving.tank.id.toString(),
                                        attr: 'receiving',
                                        value: parseFloat(receiving.volume),
                                    },
                                );
                            });
                        }
                    });
                }

                return response.data;
            });
    },

    fetchTankStocks({ commit, state }, { params, tankIds }) {
        const { since, until } = formatReconciliationDate();

        const date = params === 'opening_stock' ? since : until;

        const api = Laroute.route(
            'franchise.inventory.fuel.daily-reconciliation.report.stock',
        );

        const options = {
            outlet_id: state.activeReconciliation.outlet.id,
            date: date,
            tank_ids: tankIds,
        };

        return axios.post(api, options).then(response => {
            const tankStockResult = response.data;
            if (tankStockResult) {
                tankStockResult.forEach(stock => {
                    commit(ACTION_TYPES.UPDATE_DAILY_RECONCILIATION_DETAIL, {
                        tankId: stock.tankId,
                        attr: params,
                        value: parseFloat(stock.balance),
                    });
                });
            }

            return response.data;
        });
    },

    fetchTankDipping({ commit, state }) {
        const { since, until } = formatDate(
            state.activeReconciliation.reconciliation_date,
        );

        return axios
            .get(
                Laroute.route('franchise.inventory.fuel.dipping.list', {
                    outlet_id: state.activeReconciliation.outlet.id,
                    since: since,
                    until: until,
                }),
            )
            .then(response => {
                const tankDippingResult = response.data.data;
                if (tankDippingResult) {
                    tankDippingResult.forEach(dipping => {
                        if (
                            dipping.status === 'COMPLETED' &&
                            !dipping.deleted_at
                        ) {
                            dipping.dippings.forEach(dip => {
                                const tankIndex = findTankIndex(
                                    state.reconciliationDetails,
                                    dip.tank_id.toString(),
                                );
                                if (tankIndex !== -1) {
                                    const updatedClosingStock = [
                                        ...state.reconciliationDetails[
                                            tankIndex
                                        ].actual_closing_stock,
                                        {
                                            balance: dip.balance,
                                            active:
                                                state.reconciliationDetails[
                                                    tankIndex
                                                ].actual_closing_stock
                                                    .length === 0,
                                            dip_id: dip.id,
                                            dipping_date: dip.dipping_date,
                                        },
                                    ];
                                    commit(
                                        ACTION_TYPES.UPDATE_DAILY_RECONCILIATION_DETAIL,
                                        {
                                            tankId: dip.tank_id,
                                            attr: 'actual_closing_stock',
                                            value: updatedClosingStock,
                                        },
                                    );
                                }
                            });
                        }
                    });
                }

                return response.data;
            });
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};



// WEBPACK FOOTER //
// ./resources/assets/js/store/modules/daily-reconciliation.js